import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { reportsAPI, masterAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import SearchableSelect from '../components/ui/SearchableSelect';
import toast from 'react-hot-toast';
import { BarChart3, FileText, Package, TrendingUp, DollarSign, PieChart, ShieldCheck } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const fmt    = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const today  = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; };

const TABS = [
  { key: 'sales',         label: 'Sales Register',         icon: BarChart3 },
  { key: 'monthlygrowth', label: 'Monthly Growth Report', icon: TrendingUp },
  { key: 'purchase',      label: 'Purchase Register',     icon: FileText },
  { key: 'stock',         label: 'Stock Valuation',        icon: Package },
  { key: 'gst',           label: 'GST Summary',            icon: FileText },
];

export default function ReportsPage() {
  const location = useLocation();
  const [tab, setTab]         = useState(() => location.pathname === '/gst' ? 'gst' : 'sales');
  const [branches, setBranches] = useState([]);
  const [branchID, setBranchID] = useState('');
  const [from, setFrom]       = useState(firstOfMonth());
  const [to, setTo]           = useState(today());
  const [month, setMonth]     = useState(new Date().getMonth() + 1);
  const [year, setYear]       = useState(new Date().getFullYear());
  const [data, setData]       = useState([]);
  const [totals, setTotals]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    masterAPI.getBranches().then(r => {
      const list = r.data.data || [];
      setBranches(list);
      if (list[0]) setBranchID(list[0].branchID);
    });
  }, []);

  useEffect(() => {
    if (location.pathname === '/gst') {
      setTab('gst');
    } else if (location.pathname === '/reports') {
      setTab('sales');
    }
    setData([]);
    setTotals(null);
  }, [location.pathname]);

  const run = async () => {
    if (!branchID) return toast.error('Select branch');
    setLoading(true);
    setData([]); setTotals(null);
    try {
      let res;
      if (tab === 'sales')         res = await reportsAPI.salesRegister({ branchID, fromDate: from, toDate: to });
      if (tab === 'monthlygrowth') res = await reportsAPI.salesMonthlyGrowth({ branchID, year });
      if (tab === 'purchase')      res = await reportsAPI.purchaseRegister({ branchID, fromDate: from, toDate: to });
      if (tab === 'stock')         res = await reportsAPI.stockValuation({ branchID });
      if (tab === 'gst')           res = await reportsAPI.gstSummary({ branchID, month, year });

      if (tab === 'sales' || tab === 'purchase') {
        setData(res.data.data?.data || []);
        setTotals(res.data.data?.totals || null);
      } else {
        setData(res.data.data || []);
      }
    } catch { toast.error('Report failed'); }
    finally { setLoading(false); }
  };

  // Auto-run report whenever branchID or tab changes
  useEffect(() => {
    if (branchID) {
      run();
    }
  }, [branchID, tab, year]);

  const salesCols = [
    { header: 'INVOICE DATE (MM/DD/YYYY)', key: 'invoiceDate', render: r => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString('en-GB').replace(/\//g, '-') : '—' },
    { header: 'CUSTOMER NAME', key: 'customerName', render: r => r.customerName || '—' },
    { header: 'SALES CHANNEL', key: 'salesChannel', render: r => r.salesChannel || 'Website' },
    { header: 'SKU', key: 'skuNo', render: r => <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{r.skuNo || '—'}</code> },
    { header: 'COLLECTION', key: 'collection', render: r => r.collection || '—' },
    { header: 'COGS', key: 'cogs', align: 'right', render: r => fmt(r.cogs) },
    { header: 'MRP', key: 'mrp', align: 'right', render: r => fmt(r.mrp) },
    { header: 'DISCOUNT', key: 'discount', align: 'right', render: r => fmt(r.discount) },
    { header: 'SALE PRICE', key: 'salePrice', align: 'right', render: r => <strong>{fmt(r.salePrice)}</strong> },
    { header: 'TAXABLE AMOUNT', key: 'taxableAmount', align: 'right', render: r => fmt(r.taxableAmount) },
    { header: 'TOTAL TAX (GST)', key: 'totalTax', align: 'right', render: r => fmt(r.totalTax) },
    { header: 'GROSS PROFIT', key: 'grossProfit', align: 'right', render: r => <span style={{ color: 'var(--green-700, #15803d)', fontWeight: 700 }}>{fmt(r.grossProfit)}</span> },
    { header: 'GROSS PROFIT %', key: 'grossProfitPct', align: 'right', render: r => `${fmt(r.grossProfitPct)}%` },
    { header: 'TOTAL EXPENSES', key: 'totalExpenses', align: 'right', render: r => fmt(r.totalExpenses) },
    { header: 'NET PROFIT', key: 'netProfit', align: 'right', render: r => <span style={{ color: 'var(--green-700, #15803d)', fontWeight: 800 }}>{fmt(r.netProfit)}</span> },
    { header: 'NET PROFIT %', key: 'netProfitPct', align: 'right', render: r => <strong>{fmt(r.netProfitPct)}%</strong> },
  ];

  const monthlygrowthCols = [
    { header: 'Month', key: 'month', render: r => <span style={{ fontWeight: 700, color: 'var(--navy-900)' }}>{r.month}</span> },
    { header: 'Total COGS', key: 'totalCOGS', align: 'right', render: r => fmt(r.totalCOGS) },
    { header: 'Total Revenue', key: 'totalRevenue', align: 'right', render: r => <strong>{fmt(r.totalRevenue)}</strong> },
    { header: 'Total Expenses', key: 'totalExpenses', align: 'right', render: r => fmt(r.totalExpenses) },
    { header: 'Gross Profit', key: 'grossProfit', align: 'right', render: r => <span style={{ color: 'var(--green-700, #15803d)', fontWeight: 700 }}>{fmt(r.grossProfit)}</span> },
    { header: 'Gross Profit %', key: 'grossProfitPct', align: 'right', render: r => `${fmt(r.grossProfitPct)}%` },
    { header: 'Net Profit', key: 'netProfit', align: 'right', render: r => <span style={{ color: 'var(--green-700, #15803d)', fontWeight: 800 }}>{fmt(r.netProfit)}</span> },
    { header: 'Net Profit %', key: 'netProfitPct', align: 'right', render: r => <strong>{fmt(r.netProfitPct)}%</strong> },
    {
      header: 'Revenue Growth %', key: 'revenueGrowthPct', align: 'right', render: r => {
        if (r.revenueGrowthPct == null) return <span className="text-muted">—</span>;
        const pos = r.revenueGrowthPct > 0;
        return <span style={{ color: pos ? '#16a34a' : r.revenueGrowthPct < 0 ? '#dc2626' : '#6b7280', fontWeight: 700 }}>{pos ? `+${fmt(r.revenueGrowthPct)}%` : `${fmt(r.revenueGrowthPct)}%`}</span>;
      }
    },
    {
      header: 'GP Growth %', key: 'gpGrowthPct', align: 'right', render: r => {
        if (r.gpGrowthPct == null) return <span className="text-muted">—</span>;
        const pos = r.gpGrowthPct > 0;
        return <span style={{ color: pos ? '#16a34a' : r.gpGrowthPct < 0 ? '#dc2626' : '#6b7280', fontWeight: 700 }}>{pos ? `+${fmt(r.gpGrowthPct)}%` : `${fmt(r.gpGrowthPct)}%`}</span>;
      }
    },
    {
      header: 'NP Growth %', key: 'npGrowthPct', align: 'right', render: r => {
        if (r.npGrowthPct == null) return <span className="text-muted">—</span>;
        const pos = r.npGrowthPct > 0;
        return <span style={{ color: pos ? '#16a34a' : r.npGrowthPct < 0 ? '#dc2626' : '#6b7280', fontWeight: 700 }}>{pos ? `+${fmt(r.npGrowthPct)}%` : `${fmt(r.npGrowthPct)}%`}</span>;
      }
    },
  ];

  const purchaseCols = [
    { header: 'Purchase No', key: 'purchaseNo', render: r => <code style={{ fontSize: 12 }}>{r.purchaseNo}</code> },
    { header: 'Date', key: 'purchaseDate', render: r => new Date(r.purchaseDate).toLocaleDateString('en-IN') },
    { header: 'Supplier', key: 'supplierName' },
    { header: 'Weight', key: 'totalWeight', align: 'right', render: r => `${fmt(r.totalWeight)}g` },
    { header: 'Taxable', key: 'taxableAmount', align: 'right', render: r => `₹${fmt(r.taxableAmount)}` },
    { header: 'Total', key: 'totalAmount', align: 'right', render: r => <strong>₹{fmt(r.totalAmount)}</strong> },
    { header: 'Balance', key: 'balanceAmount', align: 'right', render: r => `₹${fmt(r.balanceAmount)}` },
    { header: 'Status', key: 'status', render: r => <span className={`badge ${r.status === 'PAID' ? 'badge-green' : 'badge-amber'}`}>{r.status}</span> },
  ];

  const stockCols = [
    { header: 'SKU No', key: 'skuNo', render: r => <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{r.skuNo || r.itemCode}</code> },
    { header: 'Item', key: 'itemName', render: r => <span style={{ fontWeight: 600 }}>{r.itemName}</span> },
    { header: 'Category', key: 'categoryName' },
    { header: 'Purity', key: 'purityCode', render: r => <span className="badge badge-navy">{r.purityCode}</span> },
    { header: 'Weight (g)', key: 'currentWeight', align: 'right', render: r => <strong>{fmt(r.currentWeight)}</strong> },
    { header: 'Avg Rate', key: 'avgRate', align: 'right', render: r => `₹${fmt(r.avgRate)}` },
    { header: 'Stock Value', key: 'stockValue', align: 'right', render: r => <span style={{ color: 'var(--navy-700)', fontWeight: 700 }}>₹{fmt(r.stockValue)}</span> },
  ];

  const gstCols = [
    { header: 'Tax Type', key: 'taxType', render: r => <span className={`badge ${r.taxType === 'OUTPUT' ? 'badge-green' : 'badge-blue'}`}>{r.taxType}</span> },
    { header: 'Taxable Amt', key: 'taxableAmount', align: 'right', render: r => `₹${fmt(r.taxableAmount)}` },
    { header: 'CGST', key: 'cgstAmount', align: 'right', render: r => `₹${fmt(r.cgstAmount)}` },
    { header: 'SGST', key: 'sgstAmount', align: 'right', render: r => `₹${fmt(r.sgstAmount)}` },
    { header: 'IGST', key: 'igstAmount', align: 'right', render: r => `₹${fmt(r.igstAmount)}` },
    { header: 'Total Tax', key: 'totalTax', align: 'right', render: r => <strong>₹{fmt(r.totalTax)}</strong> },
  ];

  const colMap = { sales: salesCols, monthlygrowth: monthlygrowthCols, purchase: purchaseCols, stock: stockCols, gst: gstCols };
  const searchMap = { sales: ['customerName','skuNo','collection','salesChannel'], monthlygrowth: ['month'], purchase: ['purchaseNo','supplierName'], stock: ['itemName','categoryName'], gst: ['taxType'] };

  const isGstRoute = location.pathname === '/gst';
  const visibleTabs = isGstRoute 
    ? TABS.filter(t => t.key === 'gst') 
    : TABS.filter(t => t.key !== 'gst');

  // Overall totals calculation for monthly growth report
  const overallRev = data.reduce((sum, r) => sum + Number(r.totalRevenue || 0), 0);
  const overallCOGS = data.reduce((sum, r) => sum + Number(r.totalCOGS || 0), 0);
  const overallExp = data.reduce((sum, r) => sum + Number(r.totalExpenses || 0), 0);
  const overallGP = data.reduce((sum, r) => sum + Number(r.grossProfit || 0), 0);
  const overallNP = data.reduce((sum, r) => sum + Number(r.netProfit || 0), 0);
  const overallGPPct = overallRev > 0 ? (overallGP / overallRev) * 100 : 0;
  const overallNPPct = overallRev > 0 ? (overallNP / overallRev) * 100 : 0;

  const chartData = data.map(r => ({
    name: r.month,
    Revenue: Number(r.totalRevenue || 0),
    COGS: Number(r.totalCOGS || 0),
    GrossProfit: Number(r.grossProfit || 0),
    NetProfit: Number(r.netProfit || 0),
    MarginPct: Number(r.netProfitPct || 0),
  }));

  return (
    <div>
      <div className="mb-6">
        <h1>{isGstRoute ? 'GST Reports' : 'Reports & Analytics'}</h1>
        <p className="text-muted">
          {isGstRoute ? 'GST transaction reports and summaries' : 'Business intelligence, visual charts, and profit growth summaries'}
        </p>
      </div>

      {/* Tabs */}
      {visibleTabs.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
          {visibleTabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setData([]); setTotals(null); }}
                className={`btn ${tab === t.key ? 'btn-primary' : 'btn-ghost'} btn-sm`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4" style={{ overflow: 'visible' }}>
        <div className="card-body" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, width: 180 }}>
              <label className="form-label">Branch</label>
              <SearchableSelect
                value={String(branchID)}
                onChange={val => setBranchID(val)}
                options={branches.map(b => ({ value: String(b.branchID), label: b.branchName }))}
                placeholder="Branch"
              />
            </div>

            {(tab === 'sales' || tab === 'purchase') && (<>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">From</label>
                <input className="form-control" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ height: 38 }} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">To</label>
                <input className="form-control" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ height: 38 }} />
              </div>
            </>)}

            {(tab === 'gst' || tab === 'monthlygrowth') && (<>
              {tab === 'gst' && (
                <div className="form-group" style={{ margin: 0, width: 140 }}>
                  <label className="form-label">Month</label>
                  <SearchableSelect
                    value={String(month)}
                    onChange={val => setMonth(Number(val))}
                    options={Array.from({ length: 12 }, (_, i) => ({ value: String(i+1), label: new Date(0, i).toLocaleString('en-IN', { month: 'long' }) }))}
                    placeholder="Month"
                  />
                </div>
              )}
              <div className="form-group" style={{ margin: 0, width: 100 }}>
                <label className="form-label">Year</label>
                <SearchableSelect
                  value={String(year)}
                  onChange={val => setYear(Number(val))}
                  options={[2024, 2025, 2026, 2027].map(y => ({ value: String(y), label: String(y) }))}
                  placeholder="Year"
                />
              </div>
            </>)}

            <button className="btn btn-primary" onClick={run} disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 15, height: 15 }} /> : <BarChart3 size={15} />}
              Run Report
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Growth Dashboard Summary Cards & Charts */}
      {tab === 'monthlygrowth' && data.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
          {/* KPI Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 12px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Revenue</span>
                <h3 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#1e1b4b', fontFamily: 'var(--mono)' }}>₹{fmt(overallRev)}</h3>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><TrendingUp size={22} /></div>
            </div>

            <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 12px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total COGS (Cost)</span>
                <h3 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#334155', fontFamily: 'var(--mono)' }}>₹{fmt(overallCOGS)}</h3>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: '#f1f5f9', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Package size={22} /></div>
            </div>

            <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 12px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Gross Profit</span>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 11, fontWeight: 800, padding: '1px 7px', borderRadius: 10 }}>{overallGPPct.toFixed(1)}%</span>
                </div>
                <h3 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#15803d', fontFamily: 'var(--mono)' }}>₹{fmt(overallGP)}</h3>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DollarSign size={22} /></div>
            </div>

            <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 12px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Net Profit</span>
                  <span style={{ background: '#ccfbf1', color: '#0f766e', fontSize: 11, fontWeight: 800, padding: '1px 7px', borderRadius: 10 }}>{overallNPPct.toFixed(1)}%</span>
                </div>
                <h3 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#0f766e', fontFamily: 'var(--mono)' }}>₹{fmt(overallNP)}</h3>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: '#ccfbf1', color: '#0f766e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={22} /></div>
            </div>
          </div>

          {/* Visual Recharts Graphs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20 }}>
            {/* Chart 1: Revenue vs COGS vs Gross Profit Bar Chart */}
            <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Monthly Revenue vs Cost vs Gross Profit ({year})</h4>
              </div>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={val => `₹${val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val}`} />
                    <Tooltip formatter={(value) => [`₹${fmt(value)}`, '']} labelStyle={{ fontWeight: 700 }} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Bar dataKey="Revenue" fill="#6366f1" radius={[4, 4, 0, 0]} name="Revenue" />
                    <Bar dataKey="COGS" fill="#94a3b8" radius={[4, 4, 0, 0]} name="COGS (Cost)" />
                    <Bar dataKey="GrossProfit" fill="#22c55e" radius={[4, 4, 0, 0]} name="Gross Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 2: Net Profit & Profit Margin Trend Line Chart */}
            <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Monthly Net Profit &amp; Margin % Trend</h4>
              </div>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="npColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={val => `₹${val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val}`} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#0f766e' }} tickFormatter={val => `${val}%`} />
                    <Tooltip formatter={(value, name) => [name === 'MarginPct' ? `${value}%` : `₹${fmt(value)}`, name]} labelStyle={{ fontWeight: 700 }} />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Area yAxisId="left" type="monotone" dataKey="NetProfit" stroke="#0d9488" fillOpacity={1} fill="url(#npColor)" name="Net Profit (₹)" />
                    <Line yAxisId="right" type="monotone" dataKey="MarginPct" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} name="Net Profit Margin %" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {data.length > 0 && (
        <div className="card">
          {totals && (
            <div className="card-header" style={{ borderBottom: '1px solid var(--silver-200)', paddingBottom: 14 }}>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                {totals.totalBills && <div><span className="text-muted">Bills: </span><strong>{totals.totalBills}</strong></div>}
                {totals.totalWeight && <div><span className="text-muted">Weight: </span><strong>{fmt(totals.totalWeight)}g</strong></div>}
                {totals.totalAmount && <div><span className="text-muted">Total: </span><strong style={{ color: 'var(--navy-700)' }}>₹{fmt(totals.totalAmount)}</strong></div>}
                {totals.totalCGST   && <div><span className="text-muted">CGST: </span><strong>₹{fmt(totals.totalCGST)}</strong></div>}
                {totals.totalSGST   && <div><span className="text-muted">SGST: </span><strong>₹{fmt(totals.totalSGST)}</strong></div>}
              </div>
            </div>
          )}
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ padding: '16px 20px' }}>
              <DataTable data={data} columns={colMap[tab]} loading={false}
                searchKeys={searchMap[tab]} pageSize={20} exportName={`report-${tab}`} noDateFilter={true} />
            </div>
          </div>
        </div>
      )}

      {!loading && data.length === 0 && (
        <div className="card">
          <div className="empty-state" style={{ padding: 64 }}>
            <BarChart3 size={48} />
            <h4>Run the report</h4>
            <p className="text-muted">Select filters and click "Run Report" to see data</p>
          </div>
        </div>
      )}
    </div>
  );
}
